import { useEffect, useState, useCallback } from 'react';
import {
  X,
  Loader2,
  Check,
  ChevronRight,
  ChevronLeft,
  Video,
  ClipboardList,
  Users,
  CalendarClock,
  CalendarCheck,
  Eye,
  Plus,
  Trash2,
  Link2,
  MapPin,
  Clock,
} from 'lucide-react';
import type { Application } from '@core/types';
import type { ApplicationStatus } from '@core/enums';
import { isApplicationStatusConflictError } from '@core/supabase/applicationStatusConflict';
import { applicationService } from '@core/supabase/services/applicationService';
import {
  interviewEventService,
  type SchedulingProvider,
} from '@core/supabase/services/interviewEventService';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import {
  defaultInterviewWizardDraft,
  loadInterviewWizardDraft,
  saveInterviewWizardDraft,
  clearInterviewWizardDraft,
  formatDaySeedForDatetimeLocal,
  type InterviewWizardDraft,
} from './interviewWizardDraft';

const BRAND = '#8B80F9';

const STEP_CONFIG = [
  { label: 'Application', icon: ClipboardList, description: 'Select the pipeline application' },
  { label: 'Panel',       icon: Users,         description: 'Lead interviewer & round' },
  { label: 'Availability',icon: CalendarClock, description: 'Capture scheduling windows' },
  { label: 'Scheduling',  icon: CalendarCheck, description: 'Book or assign a slot' },
  { label: 'Review',      icon: Eye,           description: 'Confirm & create' },
] as const;

const TOTAL = STEP_CONFIG.length;

const STEP_TIPS: string[][] = [
  [
    'Choose the candidate-job pair this interview belongs to.',
    'Only applications with an active pipeline stage are shown.',
    'Multiple rounds can be scheduled for the same application.',
  ],
  [
    'Lead interviewer details appear on confirmation emails.',
    'Additional panelists receive calendar hold notifications.',
    'Round number drives pipeline stage: R1 → Interview 1 Scheduled.',
  ],
  [
    'Recording availability helps select the best time slot.',
    'Candidate availability is stored on the interview event row.',
    'Panel availability aids cross-team scheduling coordination.',
  ],
  [
    'Self-book: share a Luma/Calendly link — candidate picks a slot.',
    'Fixed slot: set an exact time — immediately marks as scheduled.',
    'Confirmation emails are queued for candidate and all panelists.',
  ],
  [
    'Review all details carefully before creating the interview.',
    'Pipeline stage will update automatically on creation.',
    'You can reschedule or cancel later from application detail.',
  ],
];

function pipelineForScheduledRound(round: number): ApplicationStatus {
  return round >= 2 ? 'interview_2_scheduled' : 'interview_1_scheduled';
}

function appLabel(app: Application): string {
  const c = app.candidate?.full_name ?? 'Candidate';
  const j = app.job?.title ?? 'Job';
  const cl = app.client?.name ?? 'Client';
  return `${c} → ${j} (${cl})`;
}

/** Card with a branded left-border header bar */
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl overflow-hidden border vscode-border">
      <div
        className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 vscode-border border-b"
        style={{ color: BRAND }}
      >
        <div className="w-1 h-4 rounded-full shrink-0" style={{ background: BRAND }} />
        {title}
      </div>
      <div className="p-4 space-y-3">
        {children}
      </div>
    </div>
  );
}

function FieldRow({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 vscode-text-primary">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      {children}
    </div>
  );
}

export function InterviewWizardModal(props: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string | null | undefined;
  recruiterName: string | null | undefined;
  applications: Application[];
  onCreated: () => void;
  scheduleDayKey?: string | null;
  initialApplicationId?: string | null;
}) {
  const {
    open, onClose, orgId, orgName, recruiterName,
    applications, onCreated, scheduleDayKey, initialApplicationId,
  } = props;

  const [draft, setDraft] = useState<InterviewWizardDraft>(defaultInterviewWizardDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const saved = loadInterviewWizardDraft();
    const base = { ...(saved ?? defaultInterviewWizardDraft()) };
    if (initialApplicationId) base.applicationId = initialApplicationId;
    if (scheduleDayKey && /^\d{4}-\d{2}-\d{2}$/.test(scheduleDayKey)) {
      const [yy, mm, dd] = scheduleDayKey.split('-').map(Number);
      const dayDate = new Date(yy, mm - 1, dd, 9, 0, 0, 0);
      setDraft({ ...base, mode: 'fixed', fixedWhen: formatDaySeedForDatetimeLocal(dayDate), step: 0 });
    } else {
      setDraft(base);
    }
    setError(null);
  }, [open, scheduleDayKey, initialApplicationId]);

  const persist = useCallback((d: InterviewWizardDraft) => { saveInterviewWizardDraft(d); }, []);

  const updateDraft = (patch: Partial<InterviewWizardDraft>) => {
    setDraft((prev) => { const next = { ...prev, ...patch }; persist(next); return next; });
  };

  const app = applications.find((a) => a.id === draft.applicationId);

  const validateStep = (step: number): string | null => {
    if (step === 0 && !draft.applicationId) return 'Select an application to continue.';
    if (step === 1 && !draft.interviewerName.trim() && !draft.interviewerEmail.trim())
      return 'Enter at least a lead panelist name or email.';
    if (step === 3) {
      if (draft.mode === 'self_book' && !draft.bookingUrl.trim())
        return 'Paste a booking page URL or switch to fixed slot.';
      if (draft.mode === 'fixed') {
        if (!draft.fixedWhen.trim()) return 'Choose date & time for the interview.';
        if (!draft.fixedLocation.trim()) return 'Enter location or video link.';
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(draft.step);
    if (err) { setError(err); return; }
    setError(null);
    if (draft.step < TOTAL - 1) {
      const next = { ...draft, step: draft.step + 1 };
      setDraft(next); persist(next);
    }
  };

  const goBack = () => {
    setError(null);
    if (draft.step > 0) {
      const next = { ...draft, step: draft.step - 1 };
      setDraft(next); persist(next);
    }
  };

  const submit = async () => {
    const err = validateStep(3);
    if (err) { setError(err); return; }
    if (!app) { setError('Application not found.'); return; }
    setSubmitting(true); setError(null);
    try {
      const panelists = draft.panelists
        .filter((p) => p.name.trim())
        .map((p) => ({ name: p.name.trim(), email: p.email.trim() || undefined, role: 'panelist' }));
      const scheduling_meta: Record<string, string> = {};
      if (draft.candidateAvailability.trim()) scheduling_meta.candidateAvailability = draft.candidateAvailability.trim();
      if (draft.panelAvailability.trim()) scheduling_meta.panelAvailability = draft.panelAvailability.trim();

      if (draft.mode === 'fixed') {
        const iso = new Date(draft.fixedWhen).toISOString();
        const dur = Number(draft.fixedDuration) || 60;
        await interviewEventService.create(
          {
            org_id: orgId, application_id: draft.applicationId, round: draft.round,
            scheduled_at: iso, location: draft.fixedLocation.trim(), duration_minutes: dur,
            interviewer_name: draft.interviewerName.trim() || null,
            interviewer_email: draft.interviewerEmail.trim() || null,
            notes: draft.notes.trim() || null, attendees: panelists,
            ...(Object.keys(scheduling_meta).length > 0 ? { scheduling_meta } : {}),
          },
          {
            candidateName: app.candidate?.full_name, candidateEmail: app.candidate?.email ?? null,
            jobTitle: app.job?.title, clientName: app.client?.name,
            recruiterName: recruiterName ?? undefined, orgName: orgName ?? undefined,
            createdBy: recruiterName ?? null,
          }
        );
        await applicationService.updateStatus(
          draft.applicationId, pipelineForScheduledRound(draft.round),
          `Interview round ${draft.round} scheduled (wizard)`,
          undefined, undefined, app ? { expectedUpdatedAt: app.updated_at } : undefined,
        );
      } else {
        await interviewEventService.createLifecycleEntry({
          org_id: orgId, application_id: draft.applicationId, round: draft.round,
          scheduling_provider: (draft.provider as SchedulingProvider) || null,
          scheduling_booking_url: draft.bookingUrl.trim(),
          notes: draft.notes.trim() || null,
          interviewer_name: draft.interviewerName.trim() || null,
          interviewer_email: draft.interviewerEmail.trim() || null,
          attendees: panelists,
          ...(Object.keys(scheduling_meta).length > 0 ? { scheduling_meta } : {}),
        });
      }
      clearInterviewWizardDraft();
      setDraft(defaultInterviewWizardDraft());
      onCreated(); onClose();
    } catch (e) {
      if (isApplicationStatusConflictError(e)) {
        setError(`${e.message} Close the wizard and reopen to load the latest application state.`);
      } else {
        setError(e instanceof Error ? e.message : 'Could not create interview');
      }
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  const currentStep = STEP_CONFIG[draft.step];

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl"
        style={{ border: `1px solid ${BRAND}30`, background: 'var(--vscode-bg-secondary)' }}>

        {/* ── Header ── */}
        <div
          className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)` }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/20 shrink-0">
              <Video className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-white leading-tight">Schedule Interview</h2>
              <p className="text-[11px] text-white/75">{currentStep.description}</p>
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors shrink-0"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Stepper ── */}
        <div className="px-6 py-4 shrink-0 border-b vscode-border"
          style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center">
            {STEP_CONFIG.map((s, i) => {
              const Icon = s.icon;
              const done   = i < draft.step;
              const active = i === draft.step;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-all shrink-0"
                      style={
                        done
                          ? { background: BRAND, color: '#fff', boxShadow: `0 2px 8px ${BRAND}55` }
                          : active
                          ? { background: `linear-gradient(135deg, ${BRAND} 0%, #6c63ff 100%)`, color: '#fff', boxShadow: `0 0 0 4px ${BRAND}28, 0 2px 10px ${BRAND}40` }
                          : { background: 'var(--vscode-bg-tertiary)', color: 'var(--vscode-text-tertiary)', border: '1.5px solid var(--vscode-border-primary)' }
                      }
                    >
                      {done
                        ? <Check className="w-4 h-4" />
                        : <Icon className="w-4 h-4" style={{ opacity: active ? 1 : 0.5 }} />}
                    </div>
                    <span
                      className="text-[10px] font-semibold whitespace-nowrap hidden sm:block"
                      style={{ color: active ? BRAND : done ? `${BRAND}cc` : 'var(--vscode-text-tertiary)' }}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < TOTAL - 1 && (
                    <div
                      className="flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all duration-300"
                      style={{ background: done ? `linear-gradient(90deg, ${BRAND}, ${BRAND}99)` : 'var(--vscode-border-primary)' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="grid grid-cols-[1fr_220px] gap-0 p-0">

            {/* Left: Form content */}
            <div className="space-y-4 min-w-0 p-5 border-r vscode-border">

              {error && (
                <div className="text-xs rounded-lg px-3 py-2.5 flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-400">
                  <span className="font-medium shrink-0">Error:</span> {error}
                </div>
              )}

              {scheduleDayKey && (
                <div className="text-xs rounded-lg px-3 py-2.5 vscode-border border"
                  style={{ borderLeft: `3px solid ${BRAND}` }}>
                  <span className="font-semibold" style={{ color: BRAND }}>From calendar:</span>{' '}
                  <span className="vscode-text-secondary">Fixed slot pre-set for{' '}
                  {formatDateTimeGmt(new Date(`${scheduleDayKey}T12:00:00`).toISOString())}. Adjust exact time in the Scheduling step.</span>
                </div>
              )}

              {/* Step 0 – Application */}
              {draft.step === 0 && (
                <SectionCard title="Choose Application">
                  <p className="text-xs vscode-text-secondary">
                    Select the candidate-job pair this interview belongs to.
                  </p>
                  {applications.length === 0 && (
                    <div className="text-xs rounded-lg px-3 py-2 border vscode-border text-amber-500"
                      style={{ borderLeft: '3px solid #f59e0b' }}>
                      No applications found — create a candidate + job application in the pipeline first.
                    </div>
                  )}
                  <FieldRow label="Application" required>
                    <select
                      className="input w-full text-sm"
                      value={draft.applicationId}
                      onChange={(e) => updateDraft({ applicationId: e.target.value })}
                      disabled={Boolean(initialApplicationId)}
                    >
                      <option value="">Select application…</option>
                      {applications.map((a) => (
                        <option key={a.id} value={a.id}>{appLabel(a)}</option>
                      ))}
                    </select>
                  </FieldRow>
                  {app && (
                    <div className="rounded-lg p-3 space-y-1 border vscode-border"
                      style={{ borderLeft: `3px solid ${BRAND}` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BRAND }}>Selected</p>
                      <p className="text-sm font-semibold vscode-text-primary">{app.candidate?.full_name} → {app.job?.title}</p>
                      <p className="text-xs vscode-text-secondary">{app.client?.name} · {app.status.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                </SectionCard>
              )}

              {/* Step 1 – Panel */}
              {draft.step === 1 && (
                <>
                  <SectionCard title="Lead Interviewer">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <FieldRow label="Name">
                        <input className="input w-full text-sm" value={draft.interviewerName}
                          onChange={(e) => updateDraft({ interviewerName: e.target.value })}
                          placeholder="e.g. Priya Nair" />
                      </FieldRow>
                      <FieldRow label="Email">
                        <input type="email" className="input w-full text-sm" value={draft.interviewerEmail}
                          onChange={(e) => updateDraft({ interviewerEmail: e.target.value })}
                          placeholder="hm@client.com" />
                      </FieldRow>
                    </div>
                  </SectionCard>
                  <SectionCard title="Round & Panelists">
                    <FieldRow label="Round number">
                      <input type="number" min={1} className="input w-28 text-sm" value={draft.round}
                        onChange={(e) => updateDraft({ round: Math.max(1, Number(e.target.value) || 1) })} />
                      <p className="text-[10px] vscode-text-tertiary mt-1">
                        Round 1 → <em>Interview 1 Scheduled</em> · Round 2+ → <em>Interview 2 Scheduled</em>
                      </p>
                    </FieldRow>
                    <div>
                      <label className="block text-xs font-medium mb-2 vscode-text-primary">Additional panelists</label>
                      <div className="space-y-2">
                        {draft.panelists.map((row, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input className="input flex-1 text-sm" placeholder="Name" value={row.name}
                              onChange={(e) => {
                                const next = [...draft.panelists];
                                next[idx] = { ...next[idx], name: e.target.value };
                                updateDraft({ panelists: next });
                              }} />
                            <input className="input flex-1 text-sm" placeholder="Email (optional)" value={row.email}
                              onChange={(e) => {
                                const next = [...draft.panelists];
                                next[idx] = { ...next[idx], email: e.target.value };
                                updateDraft({ panelists: next });
                              }} />
                            {draft.panelists.length > 1 && (
                              <button type="button" className="p-1.5 rounded text-red-400 opacity-70 hover:opacity-100 transition-opacity"
                                onClick={() => updateDraft({ panelists: draft.panelists.filter((_, i) => i !== idx) })}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button type="button" className="mt-2 flex items-center gap-1 text-xs font-medium"
                        style={{ color: BRAND }}
                        onClick={() => updateDraft({ panelists: [...draft.panelists, { name: '', email: '' }] })}>
                        <Plus className="w-3.5 h-3.5" /> Add panelist
                      </button>
                    </div>
                  </SectionCard>
                </>
              )}

              {/* Step 2 – Availability */}
              {draft.step === 2 && (
                <>
                  <SectionCard title="Candidate Availability">
                    <p className="text-xs vscode-text-secondary">Record windows discussed with the candidate for scheduling context.</p>
                    <textarea className="input w-full text-sm min-h-[80px]" value={draft.candidateAvailability}
                      onChange={(e) => updateDraft({ candidateAvailability: e.target.value })}
                      placeholder="e.g. Weekdays after 6 pm IST, any weekend" />
                  </SectionCard>
                  <SectionCard title="Panel / Hiring Manager Availability">
                    <p className="text-xs vscode-text-secondary">Record the panel's available windows to find an overlap.</p>
                    <textarea className="input w-full text-sm min-h-[80px]" value={draft.panelAvailability}
                      onChange={(e) => updateDraft({ panelAvailability: e.target.value })}
                      placeholder="e.g. Tue–Thu 2–5 pm" />
                  </SectionCard>
                </>
              )}

              {/* Step 3 – Scheduling */}
              {draft.step === 3 && (
                <>
                  <SectionCard title="Scheduling Mode">
                    <p className="text-xs vscode-text-secondary mb-1">Choose how the interview will be booked.</p>
                    <div className="flex gap-2">
                      {(['self_book', 'fixed'] as const).map((mode) => {
                        const active = draft.mode === mode;
                        return (
                          <button key={mode} type="button"
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium border transition-all"
                            style={active
                              ? { background: `${BRAND}20`, borderColor: BRAND, color: BRAND }
                              : { background: 'var(--vscode-bg-tertiary)', borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
                            onClick={() => updateDraft({ mode })}>
                            {mode === 'self_book'
                              ? <><Link2 className="w-3.5 h-3.5" /> Self-book (Luma / Calendly)</>
                              : <><Clock className="w-3.5 h-3.5" /> Fixed slot</>}
                          </button>
                        );
                      })}
                    </div>
                  </SectionCard>

                  {draft.mode === 'self_book' ? (
                    <SectionCard title="Booking Link">
                      <FieldRow label="Scheduling tool">
                        <select className="input w-full text-sm" value={draft.provider}
                          onChange={(e) => updateDraft({ provider: e.target.value as SchedulingProvider | '' })}>
                          <option value="">Other / unspecified</option>
                          <option value="luma">Luma</option>
                          <option value="calendly">Calendly</option>
                          <option value="manual">Manual link</option>
                        </select>
                      </FieldRow>
                      <FieldRow label="Booking page URL" required>
                        <input className="input w-full text-sm" value={draft.bookingUrl}
                          onChange={(e) => updateDraft({ bookingUrl: e.target.value })}
                          placeholder="https://lu.ma/… or https://calendly.com/…" />
                      </FieldRow>
                    </SectionCard>
                  ) : (
                    <SectionCard title="Fixed Slot Details">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <FieldRow label="Start date & time" required>
                          <input type="datetime-local" className="input w-full text-sm" value={draft.fixedWhen}
                            onChange={(e) => updateDraft({ fixedWhen: e.target.value })} />
                        </FieldRow>
                        <FieldRow label="Duration (minutes)">
                          <input type="number" min={15} step={5} className="input w-full text-sm" value={draft.fixedDuration}
                            onChange={(e) => updateDraft({ fixedDuration: e.target.value })} />
                        </FieldRow>
                      </div>
                      <FieldRow label="Location / video link" required>
                        <div className="relative">
                          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 vscode-text-tertiary" />
                          <input className="input w-full text-sm pl-8" value={draft.fixedLocation}
                            onChange={(e) => updateDraft({ fixedLocation: e.target.value })}
                            placeholder="Google Meet link / office address" />
                        </div>
                      </FieldRow>
                    </SectionCard>
                  )}

                  <SectionCard title="Internal Notes">
                    <textarea className="input w-full text-sm min-h-[60px]" value={draft.notes}
                      onChange={(e) => updateDraft({ notes: e.target.value })}
                      placeholder="Context for the panel, prep notes…" />
                  </SectionCard>
                </>
              )}

              {/* Step 4 – Review */}
              {draft.step === 4 && app && (
                <SectionCard title="Interview Summary">
                  <div className="space-y-3 text-sm">
                    <div className="rounded-lg p-3 space-y-1 border vscode-border"
                      style={{ borderLeft: `3px solid ${BRAND}` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BRAND }}>Application</p>
                      <p className="font-semibold vscode-text-primary">{app.candidate?.full_name}</p>
                      <p className="text-xs vscode-text-secondary">{app.job?.title} · {app.client?.name}</p>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      {[
                        ['Round', String(draft.round)],
                        ['Mode', draft.mode === 'self_book' ? 'Self-book link' : 'Fixed slot'],
                        ['Lead interviewer', draft.interviewerName || '—'],
                        ['Lead email', draft.interviewerEmail || '—'],
                      ].map(([dt, dd]) => (
                        <div key={dt}>
                          <dt className="font-medium vscode-text-secondary">{dt}</dt>
                          <dd className="vscode-text-primary break-all">{dd}</dd>
                        </div>
                      ))}
                      {draft.mode === 'self_book' ? (
                        <div className="col-span-2">
                          <dt className="font-medium vscode-text-secondary">Booking URL</dt>
                          <dd className="vscode-text-primary break-all">{draft.bookingUrl || '—'}</dd>
                        </div>
                      ) : (
                        <>
                          <div>
                            <dt className="font-medium vscode-text-secondary">Scheduled at</dt>
                            <dd className="vscode-text-primary">{draft.fixedWhen ? formatDateTimeGmt(draft.fixedWhen) : '—'}</dd>
                          </div>
                          <div>
                            <dt className="font-medium vscode-text-secondary">Duration</dt>
                            <dd className="vscode-text-primary">{draft.fixedDuration} min</dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="font-medium vscode-text-secondary">Location</dt>
                            <dd className="vscode-text-primary">{draft.fixedLocation || '—'}</dd>
                          </div>
                        </>
                      )}
                      {draft.panelists.some(p => p.name.trim()) && (
                        <div className="col-span-2">
                          <dt className="font-medium vscode-text-secondary">Panelists</dt>
                          <dd className="vscode-text-primary">{draft.panelists.filter(p => p.name.trim()).map(p => p.name).join(', ')}</dd>
                        </div>
                      )}
                      {draft.notes.trim() && (
                        <div className="col-span-2">
                          <dt className="font-medium vscode-text-secondary">Notes</dt>
                          <dd className="vscode-text-primary whitespace-pre-wrap">{draft.notes}</dd>
                        </div>
                      )}
                    </dl>
                    <div className="rounded-lg px-3 py-2 text-xs border vscode-border vscode-text-secondary"
                      style={{ borderLeft: '3px solid #22c55e' }}>
                      Pipeline will update to{' '}
                      <strong className="vscode-text-primary">
                        {draft.mode === 'fixed'
                          ? `Interview ${draft.round >= 2 ? '2' : '1'} Scheduled`
                          : 'Awaiting booking'}
                      </strong>{' '}on creation.
                    </div>
                  </div>
                </SectionCard>
              )}
            </div>

            {/* Right: Tips + Progress */}
            <div
              className="flex flex-col gap-0 sticky top-0 self-start overflow-y-auto"
              style={{ borderLeft: `3px solid ${BRAND}`, maxHeight: 'calc(92vh - 120px)' }}
            >
              {/* Step tips */}
              <div className="p-4 space-y-3 border-b vscode-border">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: BRAND }}>
                    <currentStep.icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: BRAND }}>
                    {currentStep.label}
                  </p>
                </div>
                <ul className="space-y-2.5">
                  {STEP_TIPS[draft.step].map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: BRAND }} />
                      <p className="text-[11px] leading-relaxed vscode-text-secondary">{tip}</p>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t vscode-border" style={{ background: 'var(--vscode-bg-secondary)' }}>
          {/* Progress bar */}
          <div className="h-0.5 w-full" style={{ background: 'var(--vscode-border-primary)' }}>
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{ width: `${((draft.step + 1) / TOTAL) * 100}%`, background: `linear-gradient(90deg, ${BRAND}, #6c63ff)` }}
            />
          </div>
          <div className="flex items-center gap-3 px-5 py-3.5">
            <button type="button" className="btn-secondary flex items-center gap-1.5 text-sm"
              disabled={draft.step === 0} onClick={goBack}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex-1 flex items-center justify-center gap-1.5">
              {STEP_CONFIG.map((_, i) => (
                <div key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === draft.step ? 20 : 6,
                    height: 6,
                    background: i < draft.step ? BRAND : i === draft.step ? `linear-gradient(90deg, ${BRAND}, #6c63ff)` : 'var(--vscode-border-primary)',
                  }}
                />
              ))}
            </div>
            <span className="text-xs vscode-text-tertiary shrink-0">Step {draft.step + 1} of {TOTAL}</span>
            {draft.step < TOTAL - 1 ? (
              <button type="button" className="btn-primary flex items-center gap-1.5 text-sm" onClick={goNext}>
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button"
                className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)`, boxShadow: `0 2px 12px ${BRAND}55` }}
                disabled={submitting} onClick={() => void submit()}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? 'Creating…' : 'Create Interview'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
